import { useState, useEffect, useRef } from 'react';
import { logger } from '../utils/logger';

/**
 * @file A custom hook to detect app visibility changes.
 * This hook returns a boolean indicating if the app is visible and the time
 * since it was last visible.
 */

/**
 * Simplified hook to detect app visibility changes.
 * @returns {object} An object containing the visibility state.
 * @property {boolean} isVisible - A flag indicating if the app is visible.
 * @property {number} timeSinceLastVisible - The time in milliseconds since the app was last visible.
 */
const useAppVisibility = () => {
  const [isVisible, setIsVisible] = useState(!document.hidden);
  const [timeSinceLastVisible, setTimeSinceLastVisible] = useState(0);
  const lastHiddenAtRef = useRef(null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        lastHiddenAtRef.current = Date.now();
        setIsVisible(false);
        logger.log('App visibility: hidden');
      } else {
        const previouslyHidden = lastHiddenAtRef.current || Date.now();
        const timeInBackground = Date.now() - previouslyHidden;
        setTimeSinceLastVisible(timeInBackground);
        setIsVisible(true);
        logger.log(`App visibility: visible after ${timeInBackground}ms`);
      }
    };

    // The 'pageshow' event is crucial for handling bfcache restores on iOS
    const handlePageShow = (event) => {
      if (event.persisted) {
        logger.log('App visibility: restored from bfcache');
        handleVisibilityChange();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  return { isVisible, timeSinceLastVisible };
};

export default useAppVisibility;
